<!-- eslint-disable max-len -->

<template>
  <section class="management">
    <Navbar />
    <section class="hero fixed-nav responsive-pad ">
      <div class="hero-head pt-4 pb-4">
        <p class="title">
          Abwesenheiten
        </p>
      </div>
      <AbsenceEmailImport :teachers="teachers" @saved="$store.dispatch('Teachers/load')" />
      <b-field :label="$t('teacher-management.search')">
        <b-autocomplete :key="this.loading"
        v-model="name"
        :placeholder="$t('teacher-management.search-placeholder')"
        :keep-first="true"
        :open-on-focus="true"
        :data="search_teachers"
        @select="select($event)"
        field="fullName"
        :empty="[]"
        :clearable="true"
        @input="search(name)">
          <template #empty>Keine Ergebnisse</template>
        </b-autocomplete>
      </b-field>
      <div class="modal is-active" v-if="edit_modal && name != ''">
        <div class="modal-background" @click="reset_selected"></div>
        <div class="modal-card">
          <header class="modal-card-head has-text-centered">
            <p class="title card-title">{{ selected_teacher.name }}</p>
          </header>
          <section class="modal-card-body">
            <div>
              <b-field :label="this.$t('teacher-management.search-label')">
                <b-datepicker
                  append-to-body
                  v-model="dates"
                  :show-week-number="false"
                  :locale="'de-DE'"
                  :placeholder="this.$t('teacher-management.click-to-select')"
                  icon="calendar-today"
                  trap-focus
                  range>
                </b-datepicker>
              </b-field>
            </div>
            <div class="mt-5">
              <b-field :label="'Grund der Abwesenheit'">
                <b-dropdown aria-role="list" v-model="selected_reason">
                  <template #trigger="{ active }">
                    <b-button :label="selected_reason" class="m-0 mt-2" type="is-primary"
                    :icon-right="active ? 'menu-up' : 'menu-down'" />
                  </template>
                  <b-dropdown-item value="Krank" aria-role="listitem">Krank</b-dropdown-item>
                  <b-dropdown-item value="Beurlaubt" aria-role="listitem">Beurlaubt</b-dropdown-item>
                  <b-dropdown-item value="Fahrt" aria-role="listitem">Fahrt</b-dropdown-item>
                </b-dropdown>
              </b-field>
              <b-field label="mit Klasse">
                <b-input value="" v-model="absent_with"></b-input>
              </b-field>
            </div>
          </section>
          <footer class="modal-card-foot">
            <Button :text="$t('common.cancel')" :type="'zoom'"
            @click="reset_selected" />
            <Button v-if="is_deletable" :text="'Eintrag entfernen'" @click="del_entry()" />
            <Button :text="$t('common.save')" :type="'zoom'"
            @click="add_absent_teacher" />
          </footer>
        </div>
      </div>
      <div class="hero-body p-0 pt-5">
        <div v-if="absent_teachers.length != 0">
          <p class="subtitle">Abwesende Lehrer</p>
          <p class="edit-info">Doppelklicken zum bearbeiten</p>
          <b-table class="mt-1 app-table"
            @dblclick="edit_teacher($event)"
            :data="absent_periods"
            :row-class="(row, index) => !isAbsent(row) && 'grayed-out'"
            :bordered="false"
            :striped="false"
            :narrowed="false"
            :hoverable="true"
            :loading="false"
            :focusable="false"
            :mobile-cards="true"
            :paginated="true"
            :default-sort="['name', 'asc']"
            :per-page="25"
            :selected.sync="table_selected">
            <b-table-column field="name" label="Name" sortable v-slot="props">
                {{ props.row.name }}
            </b-table-column>
            <b-table-column field="date" label="vom" sortable v-slot="props">
                {{ dateFormat(props.row.date[0]) }}
            </b-table-column>
            <b-table-column field="date" label="bis zum" sortable v-slot="props">
                {{ dateFormat(props.row.date[1]) }}
            </b-table-column>
            <b-table-column field="absent_reason" label="Grund der Abwesenheit" v-slot="props">
                {{ props.row.absent_reason }}
            </b-table-column>
            <b-table-column label="Unterrichtshinweise" v-slot="props">
              <p v-for="(note, index) in props.row.lesson_notes" :key="index">
                {{ dateFormat(note.startDate || note.date) }}
                <template v-if="note.endDate && note.endDate !== note.startDate">
                  – {{ dateFormat(note.endDate) }}
                </template>
                · {{ note.hour }} · {{ note.class }}: {{ note.comment }}
              </p>
            </b-table-column>
          </b-table>
          <Button :text="'Plan aktualisieren'" :disabled="disable_button" @click="make_plan()"/>
        </div>
      </div>
    </section>

    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="false"
    ></b-loading>

    <Footer />
  </section>
</template>

<script>
/* eslint-disable arrow-body-style */

import moment from 'moment';
import AbsenceEmailImport from '../components/Teachers/AbsenceEmailImport.vue';
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';
import Button from '../components/Common/Button.vue';
import formatting from '../mixins/formatting';

export default {
  components: {
    AbsenceEmailImport,
    Navbar,
    Footer,
    Button,
  },

  mixins: [formatting],

  data() {
    return {
      name: '',
      selected_teacher: '',
      absent_with: '',
      dates: [],
      selected_reason: 'Krank',
      loading: false,
      edit_modal: false,
      isLoading: false,
      table_selected: {},
      disable_button: false,
    };
  },

  created() {
    this.$store.dispatch('Teachers/load');
  },

  computed: {
    teachers() {
      return this.$store.getters['Teachers/get_teachers'];
    },
    search_teachers() {
      if (this.$store.getters['Teachers/get_search_list'] === []) {
        return this.teachers;
      }
      return this.$store.getters['Teachers/get_search_list'];
    },
    absent_teachers() {
      return this.$store.getters['Teachers/absent_teachers'];
    },
    absent_periods() {
      const list = [];
      for (let i = 0; i < this.absent_teachers.length; i += 1) {
        const teacher = this.absent_teachers[i];
        for (let j = 0; j < teacher.absent.length; j += 1) {
          const period = teacher.absent[j];
          list.push({
            name: teacher.fullName,
            id: teacher.id,
            date: period.date,
            absent_reason: period.absent_reason,
            absent_index: j,
            absent_with: period.absent_with,
            lesson_notes: period.lesson_notes || [],
          });
        }
      }
      return list;
    },
    currently_absent_teachers() {
      return this.$store.getters['Teachers/get_currently_absent_teachers'];
    },
    teacher_selected() {
      if (this.selected_teacher === '') {
        return false;
      }
      return true;
    },
    is_deletable() {
      return this.selected_teacher.length !== 0;
    },
    date_selected() {
      if (this.dates.length === 0) {
        return false;
      }
      return true;
    },
    button_label() {
      if (this.selected_reason === '') {
        return this.selected_reason;
      }
      return 'Grund der Abwesenheit';
    },
  },

  methods: {
    isAbsent(period) {
      const today = moment();
      if (today.isBetween(moment(period.date[0]).subtract(1, 'days'), moment(period.date[1]), 'days', '[]')) {
        return true;
      }
      return false;
    },
    make_plan() {
      this.disable_button = true;
      this.isLoading = true;
      this.$store.dispatch('Plans/make_plan').then(() => {
        this.isLoading = false;
        this.$router.push('/dashboard');
      });
    },
    search() {
      if (this.name === '') return;
      this.$store.dispatch('Teachers/search_teacher', this.name);
    },
    select(teacher) {
      this.loading = true;
      this.selected_teacher = teacher;
      this.name = teacher.name;
      this.edit_modal = true;
    },
    reset_selected() {
      this.loading = true;
      this.selected_teacher = '';
      this.name = '';
      this.dates = [];
      this.selected_reason = 'Krank';
      this.absent_with = '';
      this.selected_teacher = '';
      this.edit_modal = false;
      this.loading = false;
    },
    edit_teacher(entry) {
      this.selected_teacher = entry;
      this.dates[0] = new Date(entry.date[0]);
      this.dates[1] = new Date(entry.date[1]);
      this.selected_reason = entry.absent_reason;
      this.absent_with = entry.absent_with;
      this.name = entry.name;
      this.edit_modal = true;
    },
    del_entry() {
      this.loading = true;
      this.$store.dispatch('Teachers/del_absent_entry', this.selected_teacher).then((r) => {
        if (r) {
          this.$buefy.toast.open({
            message: 'Eintrag entfernt!',
            type: 'is-success',
          });
          this.$store.dispatch('Teachers/load');
          this.reset_selected();
        } else {
          this.$buefy.toast.open({
            message: 'Etwas ist schiefgelaufen...',
            type: 'is-danger',
          });
        }
      });
      this.reset_selected();
    },
    add_absent_teacher() {
      if (this.date_selected) {
        this.loading = true;
        this.selected_teacher.date = this.dates;
        this.selected_teacher.absent_reason = this.selected_reason;
        this.selected_teacher.absent_with = this.absent_with;
        this.$store.dispatch('Teachers/add_absent_teacher', this.selected_teacher).then((r) => {
          if (r) {
            this.$buefy.toast.open({
              message: 'Abwesenheit eingetragen',
              type: 'is-success',
            });
            this.$store.dispatch('Teachers/load');
            this.reset_selected();
          } else {
            this.$buefy.toast.open({
              message: 'Etwas ist schiefgelaufen...',
              type: 'is-danger',
            });
          }
        });
      } else {
        this.$buefy.toast.open({
          message: 'Bitte geben Sie ein Datum an.',
          type: 'is-danger',
        });
      }
    },
  },
};
</script>

<style lang="scss">
.management {
  font-family: var(--app-font-family);
  margin-top: 3.899rem;
  .hero-body {
    min-height: 20rem !important;
  }
  .save-absense {
    float: right !important;
  }
  .edit-info {
    margin-top: -1rem;
  }
  .modal {
    .modal-card-body {
      min-height: 0;
      flex-grow: 0;
    }
    .modal-card-foot {
      flex-wrap: wrap;
    }
  }
  .grayed-out {
    opacity: 0.5;
  }
  .table {
    tr.is-selected {
      background-color: var(--secondary-color)!important;
    }
    tr {
      cursor: pointer;
    }
  }
}
</style>
